import {
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { getColumnMinWidth, isFixedColumn, resizeNeighborShares, roundShare } from "./columnLayout";
import type { TableColumnLayout, TableColumnWidths } from "./types";

export interface ColumnResizeHandleProps {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  onKeyUp: (event: KeyboardEvent<HTMLElement>) => void;
}

interface ResizeGeometry {
  columnId: string;
  neighborId: string | null;
  startShares: TableColumnWidths;
  sharePerPx: number;
  minShares: TableColumnWidths;
}

interface UseColumnResizeArgs {
  columns: TableColumnLayout[];
  shares: TableColumnWidths;
  headerRef: RefObject<HTMLDivElement | null>;
  onColumnWidthsChange?: (widths: TableColumnWidths) => void;
}

const KEYBOARD_STEP_PX = 16;
/** The held drag shares are raw but are committed rounded, so equality tolerates that rounding. */
const SHARE_EPSILON = 0.011;

/** Whether every column in the held layout has the same share in the incoming one. */
const resolvedSharesMatch = (incoming: TableColumnWidths, held: TableColumnWidths) =>
  Object.keys(held).every((id) => {
    const incomingShare = incoming[id];
    return incomingShare !== undefined && Math.abs(incomingShare - (held[id] ?? 0)) < SHARE_EPSILON;
  });

const getProportionalTrackWidth = (header: HTMLDivElement, columns: TableColumnLayout[]) =>
  columns.reduce((sum, column) => {
    const cell = header.querySelector(`[data-column-id="${column.id}"]`);
    return sum + (cell?.getBoundingClientRect().width ?? 0);
  }, 0);

const applyDelta = (geometry: ResizeGeometry, deltaPx: number) =>
  resizeNeighborShares({
    shares: geometry.startShares,
    columnId: geometry.columnId,
    neighborId: geometry.neighborId,
    deltaShare: deltaPx * geometry.sharePerPx,
    minShares: geometry.minShares,
  });

export const useColumnResize = ({ columns, shares, headerRef, onColumnWidthsChange }: UseColumnResizeArgs) => {
  const [hold, setHold] = useState<{ shares: TableColumnWidths; against: TableColumnWidths } | null>(null);
  const dragShares = hold?.shares ?? null;

  // A held post-drag layout is released as soon as the consumer's own shares agree with it, so the
  // table goes back to rendering from props and never ignores a width change made elsewhere. Shares
  // that resolve to anything else release it too: the commit did not survive (an optimistic update
  // rolled back, say), and holding a layout the consumer will never echo would strand the table on
  // widths that are not the persisted ones.
  if (hold && !resolvedSharesMatch(shares, hold.against)) setHold(null);
  const [resizingColumnId, setResizingColumnId] = useState<string | null>(null);
  // The drag listens on `window` so a pointer that leaves the handle (or the table) keeps resizing;
  // the detach is kept here so an unmount mid-drag can remove it.
  const detachDragRef = useRef<(() => void) | null>(null);
  // The layout a held arrow key has built up so far. Each repeat steps from this rather than from the
  // shares the key was first pressed against, so a held key keeps moving the boundary instead of
  // landing it in the same place every repeat.
  const keyboardLayoutRef = useRef<TableColumnWidths | null>(null);

  useEffect(() => () => detachDragRef.current?.(), []);

  const proportionalColumns = columns.filter((column) => !isFixedColumn(column));

  const measure = useCallback(
    (columnId: string): ResizeGeometry | null => {
      const header = headerRef.current;
      if (!header) return null;

      const trackPx = getProportionalTrackWidth(header, proportionalColumns);
      // A track measured at a sub-pixel width is a collapsed or mid-animation header, not something
      // to derive a scale from: dividing by it yields a share per pixel large enough that one drag
      // persists a runaway width, and a column with no stored width then inherits it through the
      // mean-share fallback. Refusing the drag outright leaves the layout untouched.
      if (trackPx < 1) return null;

      const totalShare = proportionalColumns.reduce((sum, column) => sum + (shares[column.id] ?? 0), 0);
      if (totalShare <= 0) return null;

      const sharePerPx = totalShare / trackPx;
      const index = proportionalColumns.findIndex((column) => column.id === columnId);

      return {
        columnId,
        neighborId: proportionalColumns[index + 1]?.id ?? null,
        startShares: shares,
        sharePerPx,
        minShares: proportionalColumns.reduce<TableColumnWidths>((acc, column) => {
          acc[column.id] = getColumnMinWidth(column) * sharePerPx;
          return acc;
        }, {}),
      };
    },
    [headerRef, proportionalColumns, shares]
  );

  const commit = useCallback(
    (next: TableColumnWidths) => {
      // Every proportional column is reported, not just the two that moved. A column with no stored
      // width renders at the mean of the ones that have it, so persisting only the dragged pair would
      // shift that mean and visibly jump every other column on the next render.
      onColumnWidthsChange?.(
        proportionalColumns.reduce<TableColumnWidths>((acc, column) => {
          const share = next[column.id];
          if (share !== undefined) acc[column.id] = roundShare(share);
          return acc;
        }, {})
      );
    },
    [onColumnWidthsChange, proportionalColumns]
  );

  const startDrag = useCallback(
    (geometry: ResizeGeometry, startX: number) => {
      detachDragRef.current?.();
      setResizingColumnId(geometry.columnId);

      const handleMove = (event: PointerEvent) =>
        setHold({ shares: applyDelta(geometry, event.clientX - startX), against: geometry.startShares });
      const detach = () => {
        detachDragRef.current?.();
        setResizingColumnId(null);
      };
      const finish = () => {
        detach();
        setHold(null);
      };
      const handleUp = (event: PointerEvent) => {
        // The drag layout is held, not cleared. Dropping it here would render the pre-drag widths for
        // as long as the consumer takes to echo the new ones back, which reads as a snap-back flash.
        // The hold is released once the incoming shares resolve, whether to the committed layout or,
        // if the commit failed, to something else.
        const committed = applyDelta(geometry, event.clientX - startX);
        detach();
        setHold({ shares: committed, against: geometry.startShares });
        commit(committed);
      };

      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp);
      window.addEventListener("pointercancel", finish);
      detachDragRef.current = () => {
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
        window.removeEventListener("pointercancel", finish);
        detachDragRef.current = null;
      };
    },
    [commit]
  );

  const getResizeHandleProps = useCallback(
    (columnId: string): ColumnResizeHandleProps | null => {
      if (!onColumnWidthsChange) return null;
      const column = columns.find((candidate) => candidate.id === columnId);
      if (!column || isFixedColumn(column)) return null;
      // The last proportional column has no boundary to its right — dragging there would resize the
      // column against nothing, so it shows no handle.
      if (proportionalColumns[proportionalColumns.length - 1]?.id === columnId) return null;

      return {
        onPointerDown: (event) => {
          const geometry = measure(columnId);
          if (!geometry) return;
          event.preventDefault();
          startDrag(geometry, event.clientX);
        },
        onKeyDown: (event) => {
          const direction = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
          if (direction === 0) return;
          const geometry = measure(columnId);
          if (!geometry) return;
          event.preventDefault();
          // Each step shows immediately but is not saved: a held key repeats ~30 times a second, and
          // committing per repeat would issue that many writes for one interaction. The save happens
          // once on keyup, mirroring how the pointer path commits once on release.
          const stepped = applyDelta(
            { ...geometry, startShares: keyboardLayoutRef.current ?? geometry.startShares },
            direction * KEYBOARD_STEP_PX
          );
          keyboardLayoutRef.current = stepped;
          setHold({ shares: stepped, against: shares });
        },
        onKeyUp: (event) => {
          if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
          const stepped = keyboardLayoutRef.current;
          if (!stepped) return;
          keyboardLayoutRef.current = null;
          commit(stepped);
        },
      };
    },
    [columns, commit, measure, onColumnWidthsChange, proportionalColumns, shares, startDrag]
  );

  return { dragShares, resizingColumnId, getResizeHandleProps };
};
